import { Input } from "@brg/input";
import { CommonInput, CommonLabel, FlexBox, GridBox, INPUT_TYPES, useIsMobile } from "@brg/ui";
import { BaseSyntheticEvent, FC, ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AgreeWrapper, EduSecetWrapper, LegalsLabel } from "shared/modals/styles";
import styled, { useTheme, css } from "styled-components";
import { UniversitySearchBar } from "shared/components/DropdownSearchBar/UniversitySearchBar";
import { Lang, LangObject, Tag } from "@brg/api";
import { MagicBoxRow } from "shared/components/MagicBoxRow";
import { RadioInputSource } from "shared/pages/project/components/ApprovalDialog";
import { RadioBox, RadioCircle, RadioLabel } from "shared/components/Radio/styles";
import { LangObjectArray } from "@brg/api/dist/api-core/utils/lang-utils";

const RadioWrapper = styled(FlexBox)<{ checked?: boolean}>`
    ${({ checked }) => checked && css`
        .radio-circle {
            &:after {
                content: "" !important;
                width: 5px !important;
                height: 5px !important;
                border-radius: 50% !important;
                background: ${({ theme }) => theme.projectEdit.radio.dot.color} !important;
            }
        }
    `};
`;


export const CustomForm: FC<{
    onDataChange?: (ev: React.BaseSyntheticEvent, key: string, lang?: string, activeIdx?: number) => void;
    onDateChange?: (ev: React.BaseSyntheticEvent,) => void,
    onEducationChange?: (id: string) => void;
    onSelectedEduChange?: (tag: Tag<'Local'>) => void;
    onAgreeSign?: (flag: boolean, data: any) => void;
    fullName?: string;
    email?: string;
    occupation?: string;
    country?: string;
    city?: string;
    phone?: string;
    birthDate?: string;
    telegram?: string;
    timing?: string;
    education?: string;
    competitionId?: string;
    nominationsList?: any[],
    projectName?: LangObject[];
    projectYear?: string;
    about?:LangObject[];
    createCountry?: string;
    hasSubtitles?: string;
    videoLink?: string;
    cloudLink?: string;
    consentToUse?: string,
    consentToDemonstrate?: string,
    checkboxes?: { text: ReactElement, value?: string }[]
}> = (props) => {
    const isMobile = useIsMobile();
    const theme = useTheme();
    const [time, setTime] = useState(Date.now())
    const { t, i18n: { language } } = useTranslation();
    const [agreeArr, setAgreeArr] = useState<Boolean[] | undefined>([])

    const { 
        phone, 
        fullName, 
        email, 
        telegram,
        checkboxes, 
        birthDate,
        country,
        onAgreeSign, 
        onDataChange,
        onDateChange, 
        onEducationChange, 
        onSelectedEduChange,
        nominationsList,
        projectName,
        projectYear,
        timing,
        city,
        createCountry,
        about,
        hasSubtitles,
        videoLink,
        cloudLink,
        consentToUse,
        competitionId
    } = props

    useEffect(() => {
        if (!agreeArr?.length && !!checkboxes?.length) {
            setAgreeArr(Array(checkboxes?.length).fill(false))
        }
    }, [checkboxes, time])

    const isSmallShortCompetitions = useMemo(
        () => competitionId ? [
            '1d3562abcb02489bb7b18379aeaf40b9', 
            'e25c7245287548188be20de82b93a365', 
            '111a74097f0948dfb50b5258676fb5d3', 
            '5e0f05e63e8b4122931caf06bf824beb',

            '6de0169b71524f1e907be27c8336f4ec', 
            '320687b8bc604aaab9813a2da2fbb163', 
            '158af43dae6c4874961f5f7687df1619', 
            'e4ad76cc870c4d789515747b34c16c76',
            '65c4b2d0ff4347d5b2aedeed57ea1874']
            ?.includes(competitionId) 
        : undefined 
    , [competitionId])

    const currentDate = useMemo(() => {
        const date = new Date().toLocaleDateString();
        return date.split('.').reverse().join().replaceAll(',','-' )
    } , [])

    const handleChangeAgreeArr = (idx: number, value: boolean) => {
        const valueArr = agreeArr || []
        valueArr[idx] = value;
        setAgreeArr(valueArr)
        setTime(Date.now())
    }

    useEffect(() => {
        if (agreeArr?.every((i) => !!i)) {
            onAgreeSign && onAgreeSign(true, agreeArr)
        } else {
            onAgreeSign && onAgreeSign(false, agreeArr)
        }
    } , [agreeArr, onAgreeSign, time])

    const Radio = (props: any) => {
        return (
            <RadioWrapper {...props} style={{ cursor: 'pointer' }} className={props.checked ? 'active' : ''}>
                <FlexBox style={{ position: 'relative' }}>
                    <RadioInputSource {...props as any} type={'radio'}/>
                    <RadioBox {...props as any}>
                        <RadioCircle className={'radio-circle'} {...props as any} />
                        <RadioLabel {...props as any} >
                            {props.title}
                        </RadioLabel>
                    </RadioBox>
                </FlexBox>
            </RadioWrapper>
        )
    }

    return (
        <>
            {fullName !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel label={isSmallShortCompetitions ? t('designer_edit.fields.fullName2') : t('designer_edit.fields.fullname')} />
                    {/* <LegalsLabel>{isSmallShortCompetitions ? t('designer_edit.fields.fullName2') : t('designer_edit.fields.fullname')}</LegalsLabel> */}
                    <Input.Text
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        pattern='^[А-Яа-яЁёA-Z0-9a-z\s]+$'
                        onChange={(e) => onDataChange && onDataChange(e, 'fullName')}
                        value={fullName}
                        autoFocus
                        title={`${t('reports.only_word')}`}
                        data-type="fullName"
                        maxLength={100}
                        required
                    />
                </GridBox>
            )}
            {nominationsList?.length && !!onEducationChange && !!onSelectedEduChange && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start" }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.education_place')} 
                    />
                  {/* <LegalsLabel>{t('designer_edit.fields.education_place')}</LegalsLabel> */}
                  <EduSecetWrapper>
                      <UniversitySearchBar
                          onAddClick={onEducationChange}
                          onChooseClick={onSelectedEduChange}
                          editing
                          placeholder=''
                      />
                  </EduSecetWrapper>
              </GridBox>
            )}
            {email !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.email')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.email')}</LegalsLabel> */}
                    <Input.Text
                        type="email"
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        onChange={(e) => onDataChange && onDataChange(e, 'email')}
                        pattern="^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
                        value={email}
                        data-type="email"
                        required
                    />
                </GridBox>
            )}
            {birthDate !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.birth_date')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.birth_date')}</LegalsLabel> */}
                    <Input.Text
                        type="date"
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        onChange={(e) => onDateChange && onDateChange(e)}
                        value={birthDate}
                        pattern='^[0-9]{2}[.|-][0-9]{2}[.|-][0-9]{4}$'
                        name="date"
                        className={'date-input'}
                        title="Формат даты: 00.00.0000"
                        data-type="date"
                        max={currentDate}
                        required
                    />
                </GridBox>
            )}
            {nominationsList?.length && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px',placeItems: "center start" , gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.nominations_list')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.nominations_list')}</LegalsLabel> */}
                    <FlexBox direction="column" directionMobile="column" gy={8} gyMobile={8}>
                        {nominationsList?.map(({ text, value }: { text: LangObjectArray, value: string}, idx) => {
                            return (
                                <Radio 
                                    checked={value === 'true'}
                                    title={Lang(text, language)}
                                    onClick={(e: any) => onDataChange && onDataChange(e, 'nominationsList', undefined, idx)}
                                />
                            ) 
                        })}
                    </FlexBox>
                </GridBox>
            )}
            {projectName !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.projectName_ru')} 
                    />
                   {/* <LegalsLabel>{t('designer_edit.fields.projectName_ru')}</LegalsLabel> */}
                    <Input.Text
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                        onChange={(e) => onDataChange && onDataChange(e, 'projectName', 'Ru')}
                        value={projectName?.find((i) => i?.lang === 'Ru')?.name || ''}
                        title={`${t('reports.only_word')}`}
                        data-type="projectName_ru"
                        maxLength={100}
                        required
                    />
                </GridBox>
            )}
            {projectName !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.projectName_eng')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.projectName_eng')}</LegalsLabel> */}
                    <Input.Text
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                        onChange={(e) => onDataChange && onDataChange(e, 'projectName', 'En')}
                        value={projectName?.find((i) => i?.lang === 'En')?.name || ''}
                        title={`${t('reports.only_word')}`}
                        data-type="projectName_en"
                        maxLength={100}
                        required
                    />
                </GridBox>
            )}
            {projectYear !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.projectYear')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.projectYear')}</LegalsLabel> */}
                    <Input.Text
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        pattern='^[0-9\s]+$'
                        onChange={(e) => onDataChange && onDataChange(e, 'projectYear')}
                        value={projectYear}
                        title={`${t('reports.only_number')}`}
                        data-type="projectYear"
                        maxLength={20}
                        required
                    />
                </GridBox>
            )}
            {timing !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.timing')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.timing')}</LegalsLabel> */}
                    <Input.Text
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        pattern='^[0-9\s.,]+$'
                        onChange={(e) => onDataChange && onDataChange(e, 'timing')}
                        value={timing}
                        title={`${t('reports.only_number')}`}
                        data-type="timing"
                        maxLength={20}
                        required
                    />
                </GridBox>
            )}
            {createCountry !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.createCountry')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.createCountry')}</LegalsLabel> */}
                    <Input.Text
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                        onChange={(e) => onDataChange && onDataChange(e, 'createCountry')}
                        value={createCountry}
                        title={`${t('reports.only_word')}`}
                        data-type="createCountry"
                        maxLength={100}
                        required
                    />
                </GridBox>
            )}
            {about !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.about_ru')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.about_ru')}</LegalsLabel> */}
                    <Input.Textarea
                        value={about?.find((i) => i?.lang === 'Ru')?.name || ''}
                        onChange={(e) => onDataChange && onDataChange(e, 'about', 'Ru')}
                        required
                    />
                </GridBox>
            )}
            {about !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.about_en')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.about_en')}</LegalsLabel> */}
                    <Input.Textarea
                        value={about?.find((i) => i?.lang === 'En')?.name || ''}
                        onChange={(e) => onDataChange && onDataChange(e, 'about', 'En')}
                        required
                    />
                </GridBox>
            )}
            {hasSubtitles !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px',placeItems: "center start" , gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.subtitles')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.subtitles')}</LegalsLabel> */}
                    <FlexBox direction="row" directionMobile="row" gx={8} gxMobile={8}>
                        <Radio 
                            checked={hasSubtitles === 'true'}
                            title={t('designer_edit.fields.subtitles_has')}
                            onClick={(e: any) => {
                                const fakeValue = { target: { value: 'true' }}
                                onDataChange && onDataChange(fakeValue as BaseSyntheticEvent, 'hasSubtitles')
                            }}
                            required
                        />
                        <Radio 
                            checked={hasSubtitles !== 'true'}
                            title={t('designer_edit.fields.subtitles_not')}
                            onClick={(e: any) => {
                                const fakeValue = { target: { value: 'false' }}
                                onDataChange && onDataChange(fakeValue as BaseSyntheticEvent, 'hasSubtitles')
                            }}
                            required
                        />
                    </FlexBox>
                </GridBox>
            )}
            {videoLink !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.videoLink')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.videoLink')}</LegalsLabel> */}
                    <Input.Text
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        onChange={(e) => onDataChange && onDataChange(e, 'videoLink')}
                        value={videoLink}
                        data-type="videoLink"
                        maxLength={100}
                        required
                    />
                </GridBox>
            )}
            {cloudLink !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.cloudLink')} 
                        infoText={t('designer_edit.tips.cloud_info')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.cloudLink')}</LegalsLabel> */}
                    <Input.Text
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        // pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                        onChange={(e) => onDataChange && onDataChange(e, 'cloudLink')}
                        value={cloudLink}
                        // title={`${t('reports.only_word')}`}
                        data-type="cloudLink"
                        maxLength={150}
                        required
                    />
                </GridBox>
            )}
            {consentToUse !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px',placeItems: "center start" , gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <LegalsLabel>{t('designer_edit.fields.consentToUse')}</LegalsLabel>
                    <FlexBox direction="row" directionMobile="row" gx={8} gxMobile={8}>

                        <Radio 
                            checked={consentToUse === 'true'}
                            title={t('designer_edit.fields.yes')}
                            onClick={(e: any) => {
                                const fakeValue = { target: { value: 'true' }}
                                onDataChange && onDataChange(fakeValue as BaseSyntheticEvent, 'consentToUse')
                            }}
                        />
                        <Radio 
                            checked={consentToUse !== 'true'}
                            title={t('designer_edit.fields.no')}
                            onClick={(e: any) => {
                                const fakeValue = { target: { value: 'false' }}
                                onDataChange && onDataChange(fakeValue as BaseSyntheticEvent, 'consentToUse')
                            }}
                        />
                    </FlexBox>
                </GridBox>
            )}
            {phone !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px',placeItems: "center start" , gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <LegalsLabel>{t('designer_edit.fields.phone')}</LegalsLabel>
                    <Input.Text
                        type="tel"
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        onChange={(e) => onDataChange && onDataChange(e, 'phone')}
                        value={phone}
                        data-type="phone"
                        title='89008007060'
                        pattern="[0-9]{1}[0-9]{3}[0-9]{3}[0-9]{4}"
                        required
                    />
                </GridBox>
            )}
            {city !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <LegalsLabel>{t('designer_edit.fields.city')}</LegalsLabel>
                    <Input.Text
                        type="text"
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        onChange={(e) => onDataChange && onDataChange(e, 'city')}
                        value={city}
                        pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                        name="city"
                        className={'date-input'}
                        title={`${t('reports.only_word')}`}
                        data-type="city"
                        max={currentDate}
                        required
                    />
                </GridBox>
            )}
            {!!onEducationChange && !!onSelectedEduChange && !nominationsList?.length &&  (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start" }}>
                  <LegalsLabel>{isSmallShortCompetitions ? t('designer_edit.fields.education_center2') : t('designer_edit.fields.education_center')}</LegalsLabel>
                  <EduSecetWrapper>
                      <UniversitySearchBar
                          onAddClick={onEducationChange}
                          onChooseClick={onSelectedEduChange}
                          editing
                          placeholder=''
                      />
                  </EduSecetWrapper>
              </GridBox>
            )}
            {country !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <LegalsLabel>{t('designer_edit.fields.country')}</LegalsLabel>
                    <Input.Text
                        type="text"
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        onChange={(e) => onDataChange && onDataChange(e, 'country')}
                        value={country}
                        pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                        name="country"
                        className={'date-input'}
                        title={`${t('reports.only_word')}`}
                        data-type="country"
                        required
                    />
                </GridBox>
            )}

            {telegram !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <LegalsLabel>{t('designer_edit.fields.telegram')}</LegalsLabel>
                    <Input.Text
                        type="text"
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        onChange={(e) => onDataChange && onDataChange(e, 'telegram')}
                        value={telegram}
                        pattern='^[А-Яа-яЁёA-Za-z0-9@\s]+$'
                        name="country"
                        className={'date-input'}
                        // title={`${t('reports.only_word')}`}
                        data-type="country"
                        
                    />
                </GridBox>
            )}
            {checkboxes?.length && (
                <AgreeWrapper>
                    {checkboxes?.length && checkboxes?.map(({ text, value }, idx) => {
                        const checked = agreeArr?.length ? agreeArr[idx] : false;

                        return (
                            <FlexBox direction='row' align='center'>
                                <MagicBoxRow
                                    className={'checkbox'}
                                    label={''}
                                    checked={checked as boolean}
                                    onClick={() => {
                                        handleChangeAgreeArr(idx, !checked)
                                    }}
                                    isTeamEdit
                                />
                                {text}
                        </FlexBox>
                        )
                })}
                </AgreeWrapper>
            )}
</>
    )
}import { Input } from "@brg/input";
import { CommonInput, CommonLabel, FlexBox, GridBox, INPUT_TYPES, useIsMobile } from "@brg/ui";
import { BaseSyntheticEvent, FC, ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AgreeWrapper, EduSecetWrapper, LegalsLabel } from "shared/modals/styles";
import styled, { useTheme, css } from "styled-components";
import { UniversitySearchBar } from "shared/components/DropdownSearchBar/UniversitySearchBar";
import { Lang, LangObject, Tag } from "@brg/api";
import { MagicBoxRow } from "shared/components/MagicBoxRow";
import { RadioInputSource } from "shared/pages/project/components/ApprovalDialog";
import { RadioBox, RadioCircle, RadioLabel } from "shared/components/Radio/styles";
import { LangObjectArray } from "@brg/api/dist/api-core/utils/lang-utils";

const RadioWrapper = styled(FlexBox)<{ checked?: boolean}>`
    ${({ checked }) => checked && css`
        .radio-circle {
            &:after {
                content: "" !important;
                width: 5px !important;
                height: 5px !important;
                border-radius: 50% !important;
                background: ${({ theme }) => theme.projectEdit.radio.dot.color} !important;
            }
        }
    `};
`;


export const CustomForm: FC<{
    onDataChange?: (ev: React.BaseSyntheticEvent, key: string, lang?: string, activeIdx?: number) => void;
    onDateChange?: (ev: React.BaseSyntheticEvent,) => void,
    onEducationChange?: (id: string) => void;
    onSelectedEduChange?: (tag: Tag<'Local'>) => void;
    onAgreeSign?: (flag: boolean, data: any) => void;
    fullName?: string;
    email?: string;
    occupation?: string;
    country?: string;
    city?: string;
    phone?: string;
    birthDate?: string;
    telegram?: string;
    timing?: string;
    education?: string;
    competitionId?: string;
    nominationsList?: any[],
    projectName?: LangObject[];
    projectYear?: string;
    about?:LangObject[];
    createCountry?: string;
    hasSubtitles?: string;
    videoLink?: string;
    cloudLink?: string;
    consentToUse?: string,
    consentToDemonstrate?: string,
    checkboxes?: { text: ReactElement, value?: string }[]
}> = (props) => {
    const isMobile = useIsMobile();
    const theme = useTheme();
    const [time, setTime] = useState(Date.now())
    const { t, i18n: { language } } = useTranslation();
    const [agreeArr, setAgreeArr] = useState<Boolean[] | undefined>([])

    const { 
        phone, 
        fullName, 
        email, 
        telegram,
        checkboxes, 
        birthDate,
        country,
        onAgreeSign, 
        onDataChange,
        onDateChange, 
        onEducationChange, 
        onSelectedEduChange,
        nominationsList,
        projectName,
        projectYear,
        timing,
        city,
        createCountry,
        about,
        hasSubtitles,
        videoLink,
        cloudLink,
        consentToUse,
        competitionId
    } = props

    useEffect(() => {
        if (!agreeArr?.length && !!checkboxes?.length) {
            setAgreeArr(Array(checkboxes?.length).fill(false))
        }
    }, [checkboxes, time])

    const isSmallShortCompetitions = useMemo(
        () => competitionId ? [
            '1d3562abcb02489bb7b18379aeaf40b9', 
            'e25c7245287548188be20de82b93a365', 
            '111a74097f0948dfb50b5258676fb5d3', 
            '5e0f05e63e8b4122931caf06bf824beb',

            '6de0169b71524f1e907be27c8336f4ec', 
            '320687b8bc604aaab9813a2da2fbb163', 
            '158af43dae6c4874961f5f7687df1619', 
            'e4ad76cc870c4d789515747b34c16c76',
            '65c4b2d0ff4347d5b2aedeed57ea1874']
            ?.includes(competitionId) 
        : undefined 
    , [competitionId])

    const currentDate = useMemo(() => {
        const date = new Date().toLocaleDateString();
        return date.split('.').reverse().join().replaceAll(',','-' )
    } , [])

    const handleChangeAgreeArr = (idx: number, value: boolean) => {
        const valueArr = agreeArr || []
        valueArr[idx] = value;
        setAgreeArr(valueArr)
        setTime(Date.now())
    }

    useEffect(() => {
        if (agreeArr?.every((i) => !!i)) {
            onAgreeSign && onAgreeSign(true, agreeArr)
        } else {
            onAgreeSign && onAgreeSign(false, agreeArr)
        }
    } , [agreeArr, onAgreeSign, time])

    const Radio = (props: any) => {
        return (
            <RadioWrapper {...props} style={{ cursor: 'pointer' }} className={props.checked ? 'active' : ''}>
                <FlexBox style={{ position: 'relative' }}>
                    <RadioInputSource {...props as any} type={'radio'}/>
                    <RadioBox {...props as any}>
                        <RadioCircle className={'radio-circle'} {...props as any} />
                        <RadioLabel {...props as any} >
                            {props.title}
                        </RadioLabel>
                    </RadioBox>
                </FlexBox>
            </RadioWrapper>
        )
    }

    return (
        <>
            {fullName !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel label={isSmallShortCompetitions ? t('designer_edit.fields.fullName2') : t('designer_edit.fields.fullname')} />
                    {/* <LegalsLabel>{isSmallShortCompetitions ? t('designer_edit.fields.fullName2') : t('designer_edit.fields.fullname')}</LegalsLabel> */}
                    <Input.Text
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        pattern='^[А-Яа-яЁёA-Z0-9a-z\s]+$'
                        onChange={(e) => onDataChange && onDataChange(e, 'fullName')}
                        value={fullName}
                        autoFocus
                        title={`${t('reports.only_word')}`}
                        data-type="fullName"
                        maxLength={100}
                        required
                    />
                </GridBox>
            )}
            {nominationsList?.length && !!onEducationChange && !!onSelectedEduChange && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start" }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.education_place')} 
                    />
                  {/* <LegalsLabel>{t('designer_edit.fields.education_place')}</LegalsLabel> */}
                  <EduSecetWrapper>
                      <UniversitySearchBar
                          onAddClick={onEducationChange}
                          onChooseClick={onSelectedEduChange}
                          editing
                          placeholder=''
                      />
                  </EduSecetWrapper>
              </GridBox>
            )}
            {email !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.email')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.email')}</LegalsLabel> */}
                    <Input.Text
                        type="email"
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        onChange={(e) => onDataChange && onDataChange(e, 'email')}
                        pattern="^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$"
                        value={email}
                        data-type="email"
                        required
                    />
                </GridBox>
            )}
            {birthDate !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.birth_date')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.birth_date')}</LegalsLabel> */}
                    <Input.Text
                        type="date"
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        onChange={(e) => onDateChange && onDateChange(e)}
                        value={birthDate}
                        pattern='^[0-9]{2}[.|-][0-9]{2}[.|-][0-9]{4}$'
                        name="date"
                        className={'date-input'}
                        title="Формат даты: 00.00.0000"
                        data-type="date"
                        max={currentDate}
                        required
                    />
                </GridBox>
            )}
            {nominationsList?.length && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px',placeItems: "center start" , gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.nominations_list')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.nominations_list')}</LegalsLabel> */}
                    <FlexBox direction="column" directionMobile="column" gy={8} gyMobile={8}>
                        {nominationsList?.map(({ text, value }: { text: LangObjectArray, value: string}, idx) => {
                            return (
                                <Radio 
                                    checked={value === 'true'}
                                    title={Lang(text, language)}
                                    onClick={(e: any) => onDataChange && onDataChange(e, 'nominationsList', undefined, idx)}
                                />
                            ) 
                        })}
                    </FlexBox>
                </GridBox>
            )}
            {projectName !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.projectName_ru')} 
                    />
                   {/* <LegalsLabel>{t('designer_edit.fields.projectName_ru')}</LegalsLabel> */}
                    <Input.Text
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                        onChange={(e) => onDataChange && onDataChange(e, 'projectName', 'Ru')}
                        value={projectName?.find((i) => i?.lang === 'Ru')?.name || ''}
                        title={`${t('reports.only_word')}`}
                        data-type="projectName_ru"
                        maxLength={100}
                        required
                    />
                </GridBox>
            )}
            {projectName !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.projectName_eng')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.projectName_eng')}</LegalsLabel> */}
                    <Input.Text
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                        onChange={(e) => onDataChange && onDataChange(e, 'projectName', 'En')}
                        value={projectName?.find((i) => i?.lang === 'En')?.name || ''}
                        title={`${t('reports.only_word')}`}
                        data-type="projectName_en"
                        maxLength={100}
                        required
                    />
                </GridBox>
            )}
            {projectYear !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.projectYear')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.projectYear')}</LegalsLabel> */}
                    <Input.Text
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        pattern='^[0-9\s]+$'
                        onChange={(e) => onDataChange && onDataChange(e, 'projectYear')}
                        value={projectYear}
                        title={`${t('reports.only_number')}`}
                        data-type="projectYear"
                        maxLength={20}
                        required
                    />
                </GridBox>
            )}
            {timing !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.timing')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.timing')}</LegalsLabel> */}
                    <Input.Text
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        pattern='^[0-9\s.,]+$'
                        onChange={(e) => onDataChange && onDataChange(e, 'timing')}
                        value={timing}
                        title={`${t('reports.only_number')}`}
                        data-type="timing"
                        maxLength={20}
                        required
                    />
                </GridBox>
            )}
            {createCountry !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.createCountry')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.createCountry')}</LegalsLabel> */}
                    <Input.Text
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                        onChange={(e) => onDataChange && onDataChange(e, 'createCountry')}
                        value={createCountry}
                        title={`${t('reports.only_word')}`}
                        data-type="createCountry"
                        maxLength={100}
                        required
                    />
                </GridBox>
            )}
            {about !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.about_ru')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.about_ru')}</LegalsLabel> */}
                    <Input.Textarea
                        value={about?.find((i) => i?.lang === 'Ru')?.name || ''}
                        onChange={(e) => onDataChange && onDataChange(e, 'about', 'Ru')}
                        required
                    />
                </GridBox>
            )}
            {about !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.about_en')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.about_en')}</LegalsLabel> */}
                    <Input.Textarea
                        value={about?.find((i) => i?.lang === 'En')?.name || ''}
                        onChange={(e) => onDataChange && onDataChange(e, 'about', 'En')}
                        required
                    />
                </GridBox>
            )}
            {hasSubtitles !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px',placeItems: "center start" , gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.subtitles')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.subtitles')}</LegalsLabel> */}
                    <FlexBox direction="row" directionMobile="row" gx={8} gxMobile={8}>
                        <Radio 
                            checked={hasSubtitles === 'true'}
                            title={t('designer_edit.fields.subtitles_has')}
                            onClick={(e: any) => {
                                const fakeValue = { target: { value: 'true' }}
                                onDataChange && onDataChange(fakeValue as BaseSyntheticEvent, 'hasSubtitles')
                            }}
                            required
                        />
                        <Radio 
                            checked={hasSubtitles !== 'true'}
                            title={t('designer_edit.fields.subtitles_not')}
                            onClick={(e: any) => {
                                const fakeValue = { target: { value: 'false' }}
                                onDataChange && onDataChange(fakeValue as BaseSyntheticEvent, 'hasSubtitles')
                            }}
                            required
                        />
                    </FlexBox>
                </GridBox>
            )}
            {videoLink !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.videoLink')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.videoLink')}</LegalsLabel> */}
                    <Input.Text
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        onChange={(e) => onDataChange && onDataChange(e, 'videoLink')}
                        value={videoLink}
                        data-type="videoLink"
                        maxLength={100}
                        required
                    />
                </GridBox>
            )}
            {cloudLink !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <CommonLabel 
                        label={t('designer_edit.fields.cloudLink')} 
                        infoText={t('designer_edit.tips.cloud_info')} 
                    />
                    {/* <LegalsLabel>{t('designer_edit.fields.cloudLink')}</LegalsLabel> */}
                    <Input.Text
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        // pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                        onChange={(e) => onDataChange && onDataChange(e, 'cloudLink')}
                        value={cloudLink}
                        // title={`${t('reports.only_word')}`}
                        data-type="cloudLink"
                        maxLength={150}
                        required
                    />
                </GridBox>
            )}
            {consentToUse !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px',placeItems: "center start" , gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <LegalsLabel>{t('designer_edit.fields.consentToUse')}</LegalsLabel>
                    <FlexBox direction="row" directionMobile="row" gx={8} gxMobile={8}>

                        <Radio 
                            checked={consentToUse === 'true'}
                            title={t('designer_edit.fields.yes')}
                            onClick={(e: any) => {
                                const fakeValue = { target: { value: 'true' }}
                                onDataChange && onDataChange(fakeValue as BaseSyntheticEvent, 'consentToUse')
                            }}
                        />
                        <Radio 
                            checked={consentToUse !== 'true'}
                            title={t('designer_edit.fields.no')}
                            onClick={(e: any) => {
                                const fakeValue = { target: { value: 'false' }}
                                onDataChange && onDataChange(fakeValue as BaseSyntheticEvent, 'consentToUse')
                            }}
                        />
                    </FlexBox>
                </GridBox>
            )}
            {phone !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px',placeItems: "center start" , gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <LegalsLabel>{t('designer_edit.fields.phone')}</LegalsLabel>
                    <Input.Text
                        type="tel"
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        onChange={(e) => onDataChange && onDataChange(e, 'phone')}
                        value={phone}
                        data-type="phone"
                        title='89008007060'
                        pattern="[0-9]{1}[0-9]{3}[0-9]{3}[0-9]{4}"
                        required
                    />
                </GridBox>
            )}
            {city !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <LegalsLabel>{t('designer_edit.fields.city')}</LegalsLabel>
                    <Input.Text
                        type="text"
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        onChange={(e) => onDataChange && onDataChange(e, 'city')}
                        value={city}
                        pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                        name="city"
                        className={'date-input'}
                        title={`${t('reports.only_word')}`}
                        data-type="city"
                        max={currentDate}
                        required
                    />
                </GridBox>
            )}
            {!!onEducationChange && !!onSelectedEduChange && !nominationsList?.length &&  (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start" }}>
                  <LegalsLabel>{isSmallShortCompetitions ? t('designer_edit.fields.education_center2') : t('designer_edit.fields.education_center')}</LegalsLabel>
                  <EduSecetWrapper>
                      <UniversitySearchBar
                          onAddClick={onEducationChange}
                          onChooseClick={onSelectedEduChange}
                          editing
                          placeholder=''
                      />
                  </EduSecetWrapper>
              </GridBox>
            )}
            {country !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <LegalsLabel>{t('designer_edit.fields.country')}</LegalsLabel>
                    <Input.Text
                        type="text"
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        onChange={(e) => onDataChange && onDataChange(e, 'country')}
                        value={country}
                        pattern='^[А-Яа-яЁёA-Za-z\s]+$'
                        name="country"
                        className={'date-input'}
                        title={`${t('reports.only_word')}`}
                        data-type="country"
                        required
                    />
                </GridBox>
            )}

            {telegram !== undefined && (
                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                    <LegalsLabel>{t('designer_edit.fields.telegram')}</LegalsLabel>
                    <Input.Text
                        type="text"
                        style={{
                            backgroundColor: theme.competition.input.bg,
                            color: theme.competition.input.mainText,
                        }}
                        onChange={(e) => onDataChange && onDataChange(e, 'telegram')}
                        value={telegram}
                        pattern='^[А-Яа-яЁёA-Za-z0-9@\s]+$'
                        name="country"
                        className={'date-input'}
                        // title={`${t('reports.only_word')}`}
                        data-type="country"
                        
                    />
                </GridBox>
            )}
            {checkboxes?.length && (
                <AgreeWrapper>
                    {checkboxes?.length && checkboxes?.map(({ text, value }, idx) => {
                        const checked = agreeArr?.length ? agreeArr[idx] : false;

                        return (
                            <FlexBox direction='row' align='center'>
                                <MagicBoxRow
                                    className={'checkbox'}
                                    label={''}
                                    checked={checked as boolean}
                                    onClick={() => {
                                        handleChangeAgreeArr(idx, !checked)
                                    }}
                                    isTeamEdit
                                />
                                {text}
                        </FlexBox>
                        )
                })}
                </AgreeWrapper>
            )}
</>
    )
}